/**
 * GlossaryMode — global "press ?" guided-tour overlay.
 *
 * Discovery aid for the 39+ TileInfoButton popovers scattered across
 * the app. When activated:
 *   - Pulses every `[data-testid$="-info"]` button currently visible
 *     on the page so users can see what's clickable.
 *   - Renders a side panel listing each one (title from aria-label).
 *   - Clicking a panel row scrolls the matching button into view and
 *     opens its popover.
 *
 * Activation:
 *   - Press `?` (Shift+/) anywhere outside an input/textarea
 *   - Or click the floating bottom-left "? Guide" pill
 *
 * Exit: press Esc, press `?` again, or click the X.
 *
 * Implementation note: we read aria-label rather than data-testid for
 * the panel labels because aria-label already contains the
 * human-readable title ("What is Top Active Gaps?"), strip the
 * boilerplate, and we're done. No new prop on TileInfoButton needed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { HelpCircle, X, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { usePulseHighlight } from '../../hooks';

const INFO_BUTTON_SELECTOR = 'button[data-testid$="-info"][aria-label]';
const PULSE_CLASS = 'glossary-pulse-on';

// Fire-and-forget — never await, never throw. Telemetry hiccups must
// not break the UI.
function _track(event, extras = {}) {
  try {
    api.post('/analytics/glossary/track', {
      event,
      page_path: window.location.pathname,
      ...extras,
    }).catch(() => {});
  } catch {
    /* noop */
  }
}

function _readTopics() {
  const buttons = Array.from(document.querySelectorAll(INFO_BUTTON_SELECTOR));
  return buttons.map((btn, i) => {
    const raw = btn.getAttribute('aria-label') || '';
    // Strip the "What is X?" boilerplate the TileInfoButton emits.
    const title = raw.replace(/^What is\s+/, '').replace(/\?\s*$/, '').trim() || `Topic ${i + 1}`;
    return { btn, title, testid: btn.getAttribute('data-testid') || `topic-${i}` };
  });
}

export function GlossaryMode() {
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState([]);

  const refresh = useCallback(() => {
    setTopics(_readTopics());
  }, []);

  // Toggle pulse class on every info button while open — now powered
  // by the shared `usePulseHighlight` hook.
  usePulseHighlight({
    selectors: INFO_BUTTON_SELECTOR,
    active: open,
    className: PULSE_CLASS,
  });

  // Refresh the topic list + fire telemetry when opened. (Pulse logic
  // moved to the hook above.)
  useEffect(() => {
    if (!open) return;
    refresh();
    _track('open');
  }, [open, refresh]);

  // Global keyboard handler for `?` toggle and Esc close.
  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack `?` while the user is typing in an input.
      const tag = (e.target?.tagName || '').toLowerCase();
      const editable = e.target?.isContentEditable;
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || editable) return;
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    // Custom DOM event so any UI surface (e.g. the header `?` icon)
    // can request the glossary without prop-drilling.
    const onCustomToggle = () => setOpen((v) => !v);
    const onCustomOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('glossary:toggle', onCustomToggle);
    window.addEventListener('glossary:open', onCustomOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('glossary:toggle', onCustomToggle);
      window.removeEventListener('glossary:open', onCustomOpen);
    };
  }, [open]);

  const jumpTo = (topic) => {
    _track('topic_click', {
      topic_testid: topic.testid,
      topic_title: topic.title,
    });
    topic.btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Defer the click until the scroll settles a bit so the popover
    // renders anchored to the final on-screen position.
    setTimeout(() => topic.btn.click(), 350);
  };

  return (
    <>
      {/* Right-side advertise pill is hidden as of Feb 28 2026 — the
          centered ToolkitFAB now surfaces both this and the Demo
          Toolkit. The keyboard `?` shortcut still works, and the
          panel below still renders when `glossary:open` fires. */}

      {/* Side panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-slate-900/20 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
            data-testid="glossary-mode-backdrop"
          />
          <aside
            className="fixed top-0 right-0 z-[70] h-full w-80 bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            data-testid="glossary-mode-panel"
          >
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-indigo-500" />
                  Page guide
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {topics.length === 0
                    ? 'No info popovers on this page yet.'
                    : `${topics.length} topic${topics.length === 1 ? '' : 's'} explained on this page.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Close glossary"
                data-testid="glossary-mode-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {topics.length === 0 ? (
                <div className="p-4 text-xs text-slate-500 leading-relaxed">
                  This page doesn't have any annotated metrics yet — try the home page,
                  PE/ROE/LEI/ROI Health, or the Business Case Builder.
                </div>
              ) : (
                <ol className="divide-y divide-slate-100">
                  {topics.map((t, i) => (
                    <li key={t.testid}>
                      <button
                        type="button"
                        onClick={() => jumpTo(t)}
                        className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 transition-colors flex items-center gap-2 group"
                        data-testid={`glossary-topic-${i}`}
                      >
                        <span className="text-[10px] font-mono text-slate-400 w-5">{i + 1}</span>
                        <span className="text-xs text-slate-700 flex-1 truncate">{t.title}</span>
                        <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-indigo-500 flex-shrink-0" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-200 text-[10px] text-slate-400">
              Press <kbd className="bg-slate-100 rounded px-1 font-mono">?</kbd> or{' '}
              <kbd className="bg-slate-100 rounded px-1 font-mono">Esc</kbd> to close
            </div>
          </aside>
        </>
      )}
    </>
  );
}

export default GlossaryMode;
